import type { AuthProvider } from "@pharaday/auth";
import { parseDate } from "@pharaday/utils";
import axios, { type AxiosError, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import { getCookie } from "cookies-next/client";

export interface IApiClient {
  getInstance(): AxiosInstance;
  setNamespaceHeader(namespaceId: string): void;
  setProtectedPageAccessHeader(token: string): void;
}

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * Response keys the date revival leaves verbatim for this request. The revival is shape-based (any
     * ISO-looking string in the body becomes a `Date`), so a field whose contract is "show the text the BE
     * sent" (e.g. a raw imported "1983-06-24") must be exempted by key at the call site.
     */
    parseDateIgnoreKeys?: readonly string[];
  }
}

const HTTP_UNAUTHORIZED = 401;

// Response bodies are walked for ISO-looking strings and those are revived into `Date`s (see `parseDate`);
// binary responses are passed through untouched.
const onResponse = (response: AxiosResponse): AxiosResponse => {
  if (response.config.responseType === "arraybuffer" || response.config.responseType === "blob") {
    return response;
  }

  return {
    ...response,
    data: parseDate(response.data, { ignoreKeys: response.config.parseDateIgnoreKeys }),
  };
};

const COOKIE_NAMESPACE_ID = "x-namespace-id";
const COOKIE_PUBLIC_ACCESS_TOKEN = "x-protected-page";
const isClient = typeof globalThis.window !== "undefined";

export class ApiClientService implements IApiClient {
  private axiosInstance;

  constructor(
    baseURL: string,
    private readonly authProvider?: AuthProvider
  ) {
    this.axiosInstance = axios.create({
      baseURL,
    });

    this.initializeInterceptorRequest();
    this.initializeInterceptorResponse();
  }

  getInstance() {
    return this.axiosInstance;
  }

  setNamespaceHeader(namespaceId: string) {
    this.axiosInstance.defaults.headers.common["x-namespace-id"] = namespaceId;
  }

  setProtectedPageAccessHeader(token: string) {
    this.axiosInstance.defaults.headers.common["x-protected-page"] = token;
  }

  private initializeInterceptorRequest() {
    const onRequest = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
      if (!config.headers["x-namespace-id"]) {
        if (isClient) {
          config.headers["x-namespace-id"] = getCookie(COOKIE_NAMESPACE_ID);
        }

        if (this.axiosInstance.defaults.headers.common["x-namespace-id"]) {
          config.headers["x-namespace-id"] = this.axiosInstance.defaults.headers.common["x-namespace-id"];
        }
      }

      if (!config.headers["x-protected-page"]) {
        if (isClient) {
          config.headers["x-protected-page"] = getCookie(COOKIE_PUBLIC_ACCESS_TOKEN);
        }

        if (this.axiosInstance.defaults.headers.common["x-protected-page"]) {
          config.headers["x-protected-page"] = this.axiosInstance.defaults.headers.common["x-protected-page"];
        }
      }

      if (!this.authProvider) {
        return config;
      }

      try {
        const session = await this.authProvider.getSession();
        const accessToken = session?.accessToken;

        if (accessToken && config.headers) {
          config.headers.Authorization = `Bearer ${accessToken.value}`;
        }
      } catch {
        // No session available (signed out, or the session lookup failed): send the request unauthenticated
        // and let the response interceptor decide what a 401 means.
      }

      return config;
    };

    this.axiosInstance.interceptors.request.use(onRequest);
  }

  private initializeInterceptorResponse() {
    const onResponseError = async (error: AxiosError): Promise<AxiosError> => {
      const authProvider = this.authProvider;

      // if no auth provider or access token, reject the error
      if (!authProvider) {
        throw error;
      }
      const session = await authProvider.getSession();
      if (!session?.accessToken) {
        throw error;
      }

      const prevRequestConfig = error?.config as InternalAxiosRequestConfig & { retried?: boolean };
      const isUnauthorized = error?.response?.status === HTTP_UNAUTHORIZED;
      const isRetried = prevRequestConfig && prevRequestConfig?.retried;

      const forceSignOut = async (cause: unknown) => {
        await authProvider.signOut();
        throw cause;
      };

      // if the request is unauthorized and not retried, renew the session and retry the request
      if (isUnauthorized && !isRetried) {
        prevRequestConfig.retried = true;

        try {
          const renewedSession = await authProvider.renewSession();
          const accessToken = renewedSession?.accessToken;

          if (!accessToken) {
            return await forceSignOut(error);
          }

          prevRequestConfig.headers["Authorization"] = `Bearer ${accessToken.value}`;
        } catch (renewError) {
          return forceSignOut(renewError);
        }

        return this.axiosInstance(prevRequestConfig);
      }

      // if the request is unauthorized and retried, sign out and return the error
      if (isUnauthorized && isRetried) {
        return await forceSignOut(error);
      }

      throw error;
    };

    this.axiosInstance.interceptors.response.use(onResponse, onResponseError);
  }
}
